import { Hint, percent } from 'features/sentinelDesign/designStyles'
import type { SentinelDesignMonthCoverage } from 'features/sentinelDesign/types'
import { FC } from 'react'
import styled from 'styled-components'

/** Below this observed fraction a month is too thin to conclude anything from. */
const THIN_MONTH = 0.5

const THIN_COLOR = '#f97316'
const OK_COLOR = '#22d3ee'

const Strip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem;
`

const Cell = styled.div<{ $frac: number | null }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
  min-width: 3.1rem;
  padding: 0.2rem 0.25rem;
  border: 1px solid
    ${({ theme, $frac }) =>
      $frac !== null && $frac < THIN_MONTH
        ? THIN_COLOR
        : theme.forecast.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.forecast.background2};
  font-size: 0.66rem;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Fill = styled.div<{ $frac: number | null }>`
  width: 100%;
  height: 0.25rem;
  border-radius: 2px;
  background: ${({ theme }) => theme.forecast.border};

  &::after {
    content: '';
    display: block;
    height: 100%;
    border-radius: 2px;
    width: ${({ $frac }) => Math.round(($frac ?? 0) * 100)}%;
    background: ${({ $frac }) =>
      $frac !== null && $frac < THIN_MONTH ? THIN_COLOR : OK_COLOR};
  }
`

/** Observed fraction for a month, derived from rows when not given outright. */
const monthFraction = (month: SentinelDesignMonthCoverage): number | null => {
  if (typeof month.observed_frac === 'number') return month.observed_frac
  if (
    typeof month.observed === 'number' &&
    typeof month.rows === 'number' &&
    month.rows > 0
  ) {
    return month.observed / month.rows
  }
  return null
}

/** `2024-03` → `Mar 24`, and anything unexpected passes through verbatim. */
const monthLabel = (month: string): string => {
  const match = /^(\d{4})-(\d{2})/.exec(month)
  if (!match) return month
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1)
  return `${date.toLocaleString(undefined, { month: 'short' })} ${match[1].slice(2)}`
}

export interface MonthCoverageStripProps {
  months: SentinelDesignMonthCoverage[] | undefined
}

/**
 * Per-month observation coverage for one tag, thin months outlined.
 *
 * This is the figure that matters: a single overall observed fraction reads as
 * healthy while the coverage is actually episodic, and a tag that barely exists
 * during the window of interest cannot support a conclusion about it.
 */
export const MonthCoverageStrip: FC<MonthCoverageStripProps> = ({ months }) => {
  if (!months?.length) {
    return <Hint>No per-month coverage was reported for this tag.</Hint>
  }
  const thin = months.filter((month) => {
    const frac = monthFraction(month)
    return frac !== null && frac < THIN_MONTH
  })

  return (
    <>
      <Strip>
        {months.map((month) => {
          const frac = monthFraction(month)
          return (
            <Cell
              key={month.month}
              $frac={frac}
              title={`${month.month}: ${percent(frac, 1)} observed`}>
              <span>{monthLabel(month.month)}</span>
              <Fill $frac={frac} />
              <span>{percent(frac)}</span>
            </Cell>
          )
        })}
      </Strip>
      {thin.length > 0 && (
        <Hint>
          {thin.length} of {months.length} months are under{' '}
          {percent(THIN_MONTH)} observed — treat conclusions drawn over{' '}
          {thin.length === 1 ? 'that month' : 'those months'} as unsupported.
        </Hint>
      )}
    </>
  )
}
